import * as Sentry from '@sentry/react';
import { useState, type ReactNode, type JSX } from 'react';
import { AlertTriangle, Loader2, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { isChunkLoadError } from '@lib/lazyWithReload';
import { maybeReloadForStaleChunk } from '@lib/chunkReloadRecovery';

/**
 * Scoped error boundary for a feature route. Wraps Sentry.ErrorBoundary
 * with a card-shaped fallback that keeps the user inside <AppLayout> when
 * a single feature's render throws — instead of bubbling up to the
 * top-level boundary which replaces the entire shell.
 * Use one of these per `<Route>` in app/routes.tsx. The `featureName` shows
 * up in the user-facing message and in the Sentry tag so we can slice
 * incidents by feature.
 * On reset(), `resetKeys` of [pathname] forces a re-mount of the wrapped
 * subtree when the user navigates — so a transient render error doesn't
 * stick after they click around. The "Try again" button rebuilds the
 * subtree in place via the Sentry fallback's `resetError` callback.
 */
interface Props {
  featureName: string;
  /** Optional: keys that force the boundary to reset (e.g. route pathname). */
  resetKeys?: ReadonlyArray<unknown>;
  children: ReactNode;
}

/**
 * Chunk-aware fallback. A stale-chunk error (a hashed asset
 * purged by a redeploy) is not a real render failure — the page is about to
 * recover via a one-shot reload (`maybeReloadForStaleChunk`). Showing the
 * scary "failed to load" card for the ~1s the reload takes reads as a crash,
 * so for that case we render the same benign spinner the route's Suspense
 * fallback uses. This is the catch-all for any chunk error that reaches a
 * boundary from ANY path (route chunks are already intercepted earlier by
 * `lazyWithReload`). The decision latches once per mount via the useState
 * initializer; the recovery guard's own latch + 10s debounce ensure a chunk
 * error recurring right after a reload returns `false` → the real card shows
 * (no reload loops).
 */
function FallbackContent({
  featureName,
  error,
  resetError,
}: {
  featureName: string;
  error: unknown;
  resetError: () => void;
}): JSX.Element {
  const [reloading] = useState(
    () => isChunkLoadError(error) && maybeReloadForStaleChunk(),
  );

  if (reloading) {
    return (
      <div
        className="flex h-full min-h-[40vh] items-center justify-center gap-2 text-muted-foreground"
        data-testid={`feature-error-${featureName}-reloading`}
      >
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-[40vh] items-center justify-center p-6"
      data-testid={`feature-error-${featureName}`}
    >
      <Card className="max-w-lg">
        <CardHeader className="flex flex-row items-center gap-3">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300"
            aria-hidden
          >
            <AlertTriangle className="h-5 w-5" />
          </span>
          <CardTitle>This section failed to load.</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Something went wrong rendering the <strong>{featureName}</strong> view. The rest of
            the app is still usable — try the buttons below, or navigate to another section.
          </p>
          {error instanceof Error && error.message && (
            <pre
              className="overflow-x-auto rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground"
              data-testid={`feature-error-${featureName}-message`}
            >
              {error.message}
            </pre>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => resetError()}
              data-testid={`feature-error-${featureName}-retry`}
            >
              <RefreshCcw className="h-4 w-4" />
              Try again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.reload()}
              data-testid={`feature-error-${featureName}-reload`}
            >
              Reload page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function FeatureErrorBoundary({ featureName, resetKeys, children }: Props): JSX.Element {
  return (
    <Sentry.ErrorBoundary
      // Tag every captured error with the feature so Sentry's dashboard
      // groups them per surface.
      beforeCapture={(scope) => {
        scope.setTag('feature', featureName);
      }}
      {...(resetKeys ? { resetKeys } : {})}
      fallback={({ resetError, error }) => (
        <FallbackContent featureName={featureName} error={error} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
